import React, { useEffect, useState } from "react";
import { createContext } from "react";
import { getCart } from "../util/cart";

const Context = createContext();

const CartContext = ({ data, children }) => {
  const [panel, setPanel] = useState("[data-mini-cart-panel]");
  const [cartState, setCartState] = useState({});
  const [cartLoading, setCartLoading] = useState(false);

  useEffect(() => {
    setCartLoading(true);

    document.addEventListener(
      "updateCart",
      (e) => {
        update();
      },
      false
    );

    update();
  }, []);

  const getProductInfo = async (response) => {
    const itemsWithProductData = await Promise.all(
      response.items.map((item) => {
        return fetch(`/products/${item.handle}.json`)
          .then((response) => response.json())
          .then((data) => {
            return {
              ...item,
              ...data,
            };
          });
      })
    );

    return {
      ...response,
      items: itemsWithProductData,
    };
  };

  const update = () => {
    setCartLoading(true);
    getCart().then((response) => {
      updateCartCount(response.item_count);
      setCartState(response);
      setCartLoading(false);
    });
  };

  const updateCartCount = (count) => {
    const cartCount = document.querySelector("[data-header-cart-count]");
    if (cartCount == null) return;
    cartCount.innerHTML = count > 0 ? `<span>${count}</span>` : `<span>0</span>`;
  };

  const updateCart = async ({
    item = null,
    items = null,
    note = null,
    callback = {},
    updateType = "add",
  }) => {
    if (!item && !items && !note) {
      return console.error("No items were passed to the ATC request. 123");
    }

    if (item?.skuToValidate && window.QuantityLimit) {
      const canUpdateQuantity = await window.QuantityLimit.validateQuantityLimit(
        item.skuToValidate
      );
      // check is async because it needs to fetch the cart first
      if (!canUpdateQuantity) {
        return;
      }
    }

    if (item?.skuToValidate) {
      delete item.skuToValidate;
    }

    setCartLoading(true);
    if (callback?.onBefore) callback.onBefore();

    const payload =
      updateType === "add"
        ? { items: items ? items : [item] }
        : !note
        ? { updates: items ? items : item }
        : { note: note };

    return fetch(`/cart/${updateType}.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then((response) => {
        getCart().then((response) => {
          handleSuccess({ response, callback });
        });
        return response;
      })
      .catch((error) => {
        handleError(error, callback);
      });
  };

  const changeCart = async ({ item = null, items = null, callback = {} }) => {
    if (!item && !items) {
      return console.error("No items were passed to the ATC request.");
    }

    setCartLoading(true);
    if (callback?.onBefore) callback.onBefore();

    const changes = items ? items : [item];

    const changeRequest = async (payload) =>
      await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

    try {
      for (const i of changes) {
        await changeRequest(i);
      }

      getCart().then((response) => {
        handleSuccess({ response, callback });
      });
    } catch (error) {
      handleError(error, callback);
    }
  };

  const handleSuccess = async ({ response, callback }) => {
    if (callback?.onAfter) callback.onAfter(response);

    updateCartCount(response.item_count);
    setCartState(response);
    setCartLoading(false);
    document.dispatchEvent(new CustomEvent("cart:updated"));
  };

  const handleError = (error, callback = {}) => {
    console.error(error);
    if (callback?.onError) callback.onError(error);
    setCartLoading(false);
  };

  const addItem = (data) => {
    return updateCart({ ...data, updateType: "add" });
  };

  const removeItem = (data) => {
    return updateCart({ ...data, updateType: "update" });
  };

  const updateItem = (data) => {
    return updateCart({ ...data, updateType: "update" });
  };

  const changeItem = (data) => {
    return changeCart({ ...data });
  };

  return (
    <Context.Provider
      value={{
        cartState,
        cartLoading,
        panel,
        shipping: data.shippingThreshold,
        recommended: data.recommended,
        empty: data.empty,
        locales: data.locales,
        customerIsRegister: data.customerIsRegister,
        customerTags: data.customerTags,
        hasUsedDiscount: data.hasUsedDiscount,
        discountProducts: data.discountProducts,
        additionalDiscount: data.additionalDiscount,
        subscriptionDiscountCode: data.subscriptionDiscountCode,
        data,
        setPanel,
        setCartState,
        addItem,
        removeItem,
        updateItem,
        changeItem,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export const withCartContext = (Rendered) => (props) =>
  <Context.Consumer>{(value) => <Rendered {...props} {...value} />}</Context.Consumer>;

export default CartContext;
